import { useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAuth } from "@/hooks/Auth";
import { InputForm } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import loginSchema from "@/schema/loginSchema";
import { errorHandler } from "@/utils/errorhandler";
import { LoginType } from "./types";
import Logo from "@/assets/images/logo_512.png";
import React, { useState } from "react";
import { SelectUnidadeModal } from "@/components/ui/SelectUnidadeModal";
import { CHAVES, SESSAO, storage } from "@/services/sessionConfig";
import { getSessionExpiryMs } from "@/utils/jwt";

/**
 * Rota a restaurar após o relogin, gravada pelo signOut quando a sessão foi
 * encerrada involuntariamente. Fica em sessionStorage (por aba) para que uma
 * aba não redirecione a outra para um destino inesperado.
 */
const consumirRotaDeRetorno = (): string => {
  try {
    const rota = sessionStorage.getItem(CHAVES.REDIRECT);
    sessionStorage.removeItem(CHAVES.REDIRECT);
    return rota || "/";
  } catch {
    return "/";
  }
};

export const Login = () => {
  const { signIn, setAuthUser, loadingSignIn } = useAuth();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [unidades, setUnidades] = useState<any[]>([]);
  const [userData, setUserData] = useState<any>(null);

  const methods = useForm<LoginType>({
    resolver: yupResolver(loginSchema),
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = methods;

  // Redirecionar se já estiver logado. A sessão expirada é ignorada aqui: sem
  // esta checagem, um token vencido no storage prenderia o usuário fora do
  // login até a primeira requisição falhar.
  React.useEffect(() => {
    const user = storage.lerJson(CHAVES.USER);
    if (!user) return;
    const expiraEm = getSessionExpiryMs(user);
    if (expiraEm !== null && expiraEm - SESSAO.CLOCK_SKEW_MS <= Date.now()) return;
    navigate("/", { replace: true });
  }, [navigate]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await signIn(data.email, data.password);
      const user = JSON.parse(localStorage.getItem("@MatriculaWeb:user") as any);
      setUserData(user);
      const unidadesUser = user?.usuario?.unidades || [];
      setUnidades(unidadesUser);
      if (unidadesUser.length > 1) {
        setShowModal(true);
      } else if (unidadesUser.length === 1) {
        localStorage.setItem("UnidadeLogada", JSON.stringify(unidadesUser[0]));
        setAuthUser(user);
        navigate(consumirRotaDeRetorno());
      } else {
        setAuthUser(user);
        navigate(consumirRotaDeRetorno());
      }
    } catch (error: any) {
      errorHandler(error);
    }
  });

  const handleConfirmUnidade = (unidadeSelecionada: any) => {
    localStorage.setItem("UnidadeLogada", JSON.stringify(unidadeSelecionada));
    setShowModal(false);
    setAuthUser(userData);
    navigate(consumirRotaDeRetorno());
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        <div className="flex w-screen h-screen">
          <div className="w-1/2 flex justify-center items-center">
            <img
              src={Logo}
              alt="Logo Academia das Especificas"
              className="flex justify-center items-center"
              width={360}
            />
          </div>
          <div className="w-1/2 flex justify-center items-center bg-white">
            <div className="w-2/3 flex flex-col gap-4 ">
              <p className="text-5xl text-gray-950 text-start mb-10">Login</p>
              <InputForm
                type="email"
                placeholder="Email"
                {...register("email")}
              />
              <span className="text-red-600 text-start">
                {errors.email?.message}
              </span>

              <InputForm
                type="password"
                placeholder="Password"
                {...register("password")}
              />

              <span className="text-red-600 text-start">
                {errors.password?.message}
              </span>

              <Button
                className="mt-8 bg-black text-white hover:bg-black"
                variant="default"
                type="submit"
                disabled={loadingSignIn}
              >
                {loadingSignIn ? "Entrando..." : "Entrar"}
              </Button>
            </div>
          </div>
        </div>
        {showModal && (
          <SelectUnidadeModal
            unidades={unidades}
            onConfirm={handleConfirmUnidade}
          />
        )}
      </form>
    </FormProvider>
  );
};
